import Modal from '@components/styled-UI/Modal';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SavedIcon from '@images/svg-icons/savedIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CalendarIcon from '@images/svg-icons/calendarIcon.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';
import {
    IAddUpdatePrescriptionPayload,
    GetSKUPayload,
    IMySavedPrescriptionItem,
    UploadPrescriptionFilePayload,
    IContactLensDetails,
} from '@interfaces/myAppointmentInterfaces';
import { Checkbox, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as actions from '@redux-actions/';
import {
    addPrescription,
    downloadPrescriptionFile,
    getCLDetails,
    getHowToReadEspotCl,
    getSKU,
    uploadPrescriptionFile,
} from '@services/myAppointmentServices';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import {
    capitalizeFirstLetter,
    getFileFromBase64,
    getFileDataFromBase64,
    getLabelFallback,
    isValidNameOnlyLetters,
    clearPrescriptionFileName,
} from '@utils/utils';
import { compareAsc, differenceInYears, isValid } from 'date-fns';
import { SyntheticEvent, useEffect, useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'src/store/reducers';
import DoctorDetails from './DoctorDetails';
import HowToSubmit from './HowToSubmit';
import PrescriptionContactFormBox from './PrescriptionContactFormBox';
import HowToReadPrescriptionCL from './HowToReadPrescriptionCL';
import { getStringFromDate } from '@utils/dateUtils';
import {
    PRESCRIPTION_DEFAULT_NICKNAME,
    PrescriptionClass,
    PrescriptionOrigin,
    PrescriptionOriginMode,
    PrescriptionProductCategory,
} from '@utils/const';
import { editPrescription } from '@services/myAppointmentServices';
import InfoIcon from '@images/svg-icons/infoIcon.svg';

interface Form {
    firstName: string;
    lastName: string;
    dob: Date;
}

type PrescriptionContactFormProps = {
    isEditingPrescription: boolean;
    checkedDifferentCL: boolean;
    selectedRightCL?: any;
    selectedLeftCL?: any;
    editedPrescription?: IMySavedPrescriptionItem;
    editedRightCLDetails?: IContactLensDetails;
    editedLeftCLDetails?: IContactLensDetails;
    backToStepTwoFirstName?: any;
    setBackToStepTwoFirstName?: any;
    backToStepTwoLastName?: any;
    setBackToStepTwoLastName?: any;
    backToStepTwoDob?: any;
    setBackToStepTwoDob?: any;
    backToStepTwoDoctor?: any;
    setBackToStepTwoDoctor?: any;
    backToStepTwoPowerOD?: any;
    setBackToStepTwoPowerOD?: any;
    backToStepTwoPowerOS?: any;
    setBackToStepTwoPowerOS?: any;
    backToStepTwoBcOD?: any;
    setBackToStepTwoBcOD?: any;
    backToStepTwoBcOS?: any;
    setBackToStepTwoBcOS?: any;
    backToStepTwoDiaOD?: any;
    setBackToStepTwoDiaOD?: any;
    backToStepTwoDiaOS?: any;
    setBackToStepTwoDiaOS?: any;
    backToStepTwoCylOD?: any;
    setBackToStepTwoCylOD?: any;
    backToStepTwoCylOS?: any;
    setBackToStepTwoCylOS?: any;
    backToStepTwoColorOD?: any;
    setBackToStepTwoColorOD?: any;
    backToStepTwoColorOS?: any;
    setBackToStepTwoColorOS?: any;
    backToStepTwoAxisOD?: any;
    setBackToStepTwoAxisOD?: any;
    backToStepTwoAxisOS?: any;
    setBackToStepTwoAxisOS?: any;
    backToStepTwoAddOD?: any;
    setBackToStepTwoAddOD?: any;
    backToStepTwoAddOS?: any;
    setBackToStepTwoAddOS?: any;
    backToStepTwoDomOD?: any;
    setBackToStepTwoDomOD?: any;
    backToStepTwoDomOS?: any;
    setBackToStepTwoDomOS?: any;
    isDifferentCLLeftFromBack?: boolean;
    isDifferentCLRightFromBack?: boolean;
};

const PrescriptionContactForm = (props: PrescriptionContactFormProps): JSX.Element => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const howToReadEspotCl = useSelector((state: RootState) => state.myAppointment.howToReadEspotCl);
    const clDetailRight = useSelector((state: RootState) => state.myAppointment.clDetailRight);
    const clDetailLeft = useSelector((state: RootState) => state.myAppointment.clDetailLeft);

    const [isOpenDatePicker, setIsOpenDatePicker] = useState<boolean>(false);
    const [isSavingPrescription, setIsSavingPrescription] = useState(false);
    const [isCheckConfirm, setIsCheckConfirm] = useState(false);
    const [isUploadClicked, setIsUploadClicked] = useState(true);
    const [isHowToReadModalOpen, setIsHowToReadModalOpen] = useState(false);

    const defaultFormValues: Form = {
        firstName: '',
        lastName: '',
        dob: null,
    };

    const initialFormStatus = {
        firstName: {
            focus: false,
            success: false,
        },
        lastName: {
            focus: false,
            success: false,
        },
        dob: {
            focus: false,
            success: false,
        },
    };

    const [formStatus, setFormStatus] = useState(initialFormStatus);

    const methods = useForm<Form>({
        //resolver: yupResolver(schema),
        mode: 'onBlur',
        defaultValues: defaultFormValues,
    });

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        clearErrors,
        setError,
        watch,
        reset,
        control,
        formState: { errors },
    } = methods;

    const watchFirstName = watch('firstName');
    const watchLastName = watch('lastName');
    const watchDob = watch('dob');

    const [formErrors, setFormErrors] = useState({});
    const [prescriptionErrorAll, setPrescriptionErrorAll] = useState(null);

    const [doctor, setDoctor] = useState(null);
    const [doctorError, setDoctorError] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadFileImage, setUploadFileImage] = useState(null);
    const [uploadFileError, setUploadFileError] = useState(false);
    const [errorOnCreate, setErrorOnCreate] = useState(null);
    const [uploadPrescriptionCl, setUploadPrescriptionCl] = useState(false);

    const [comment, setComment] = useState(null);

    const handleChangeComment = (event) => {
        setComment(event.target.value);
    }

    useEffect(() => {
        dispatch(getHowToReadEspotCl(config.mockRequest, config.storeId));

        if (!!props.selectedRightCL) {
            dispatch(
                getCLDetails(
                    config.mockRequest,
                    config.storeId,
                    config.catalogId,
                    props.selectedRightCL,
                    true,
                    props.checkedDifferentCL
                )
            );

            if (props.checkedDifferentCL && !!props.selectedLeftCL) {
                dispatch(
                    getCLDetails(
                        config.mockRequest,
                        config.storeId,
                        config.catalogId,
                        props.selectedLeftCL,
                        false,
                        null
                    )
                );
            }
        }

        return () => {
            actions.getPDEspotSuccess(null);
            actions.getHowToReadEspotClSuccess(null);
        };
    }, []);

    useEffect(() =>{
        props.setBackToStepTwoFirstName(watchFirstName)
    }, [watchFirstName])

    useEffect(() =>{
        props.setBackToStepTwoLastName(watchLastName)
    }, [watchLastName])

    useEffect(() =>{
        props.setBackToStepTwoDob(watchDob)
    }, [watchDob])

    useEffect(() => {
        props.setBackToStepTwoDoctor(doctor)
    }, [doctor])

    const handleSetDoctor = (value) => {
        setDoctor(value);
        if (value != null) setDoctorError(false);
    };

    const handleSetUploadFile = (value) => {
        setUploadFile(value);
        if (value != null) setUploadFileError(false);
    };

    useEffect(() => {
        /**
         * Load edited prescription data in form
         */
        if (!!props.isEditingPrescription && !!props?.editedPrescription) {
            const currentEditedPrescription = props?.editedPrescription;

            reset({
                firstName: currentEditedPrescription?.firstName,
                lastName: currentEditedPrescription?.lastName,
                dob: !!props?.editedPrescription?.birthDate
                    ? new Date(props?.editedPrescription?.birthDate)
                    : null,
            });

            setFormStatus({
                ...formStatus,
                firstName: {
                    success: currentEditedPrescription?.firstName.trim() !== '',
                    focus: true,
                },
                lastName: {
                    success: currentEditedPrescription?.lastName.trim() !== '',
                    focus: true,
                },
                dob: { success: !!props?.editedPrescription?.birthDate, focus: true },
            });

            // Load right eye attributes in form
            if (!!props?.editedRightCLDetails) {
                setPowerOD(props?.editedRightCLDetails.attributes?.power);
                setBcOD(props?.editedRightCLDetails?.attributes?.baseCurve);
                setDiaOD(props?.editedRightCLDetails?.attributes?.diameter);
                setCylOD(props?.editedRightCLDetails?.attributes?.cylinder);
                setAxisOD(props?.editedRightCLDetails?.attributes?.axis);
                setAddOD(props?.editedRightCLDetails?.attributes?.add);
                setColorOD(props?.editedRightCLDetails?.attributes?.color);
            }

            // Load left eye attributes in form
            if (!!props?.editedLeftCLDetails) {
                setPowerOS(props?.editedLeftCLDetails.attributes?.power);
                setBcOS(props?.editedLeftCLDetails?.attributes?.baseCurve);
                setDiaOS(props?.editedLeftCLDetails?.attributes?.diameter);
                setCylOS(props?.editedLeftCLDetails?.attributes?.cylinder);
                setAxisOS(props?.editedLeftCLDetails?.attributes?.axis);
                setAddOS(props?.editedLeftCLDetails?.attributes?.add);
                setColorOS(props?.editedLeftCLDetails?.attributes?.color);
            }

            // Load doctor details from edited prescription
            if (
                !!currentEditedPrescription?.doctor &&
                !!currentEditedPrescription?.clinic &&
                !!currentEditedPrescription?.doctorAddress &&
                (!!currentEditedPrescription?.telephone || !!currentEditedPrescription?.doctorFax)
            ) {
                setDoctor({
                    name: currentEditedPrescription?.doctor,
                    officeName: currentEditedPrescription?.clinic,
                    address: currentEditedPrescription?.doctorAddress,
                    phoneNumber: currentEditedPrescription?.telephone,
                    faxNumber: currentEditedPrescription?.doctorFax,
                });
            }

            setComment(
                !!currentEditedPrescription.comment
                    ? currentEditedPrescription.comment
                    : null
            )

            // Load uploaded prescription file or set call doctor selected based on the edited prescription
            if (
                currentEditedPrescription?.prescriptionOriginMode ===
                PrescriptionOriginMode.UPLOAD_PRESCRIPTION &&
                !!currentEditedPrescription?.fileName
            ) {
                setIsUploadClicked(true);

                downloadPrescriptionFile(
                    config.mockRequest,
                    config.storeId,
                    currentEditedPrescription?.fileName
                )
                    .then((response: any) => {
                        if (!!response?.prescriptionFile) {
                            getFileFromBase64(
                                response?.prescriptionFile,
                                currentEditedPrescription?.fileName
                            )
                                .then((prescriptionFile: File) => {
                                    setUploadFile(prescriptionFile);
                                })
                                .catch(error => {
                                    console.error('>>> Error parsing prescription file', error);
                                });
                        } else {
                            console.error('>>> Error finding prescription file');
                        }
                    })
                    .catch(error => {
                        console.error('>>> Error downloading prescription file', error);
                    });
            } else if (
                currentEditedPrescription?.prescriptionOriginMode ===
                PrescriptionOriginMode.CALL_DOCTOR
            ) {
                setIsUploadClicked(false);
            }
        }
    }, [reset]);

    // create callbacks for the "how to read prescription" modal
    useEffect(() => {
        // @ts-expect-error
        window.astigmatismClicked = () => {
            var element = document.getElementsByClassName('astigmatism-content-panel')[0];
            var expandIconAstigmatism = document.getElementsByClassName(
                'expand-icon-astigmatism-container'
            )[0];
            var reduceIconAstigmatism = document.getElementsByClassName(
                'reduce-icon-astigmatism-container'
            )[0];

            if (element.classList.contains('display-astigmatism-panel')) {
                element.classList.remove('display-astigmatism-panel');
                expandIconAstigmatism.classList.remove('hide');
                reduceIconAstigmatism.classList.add('hide');
            } else {
                element.classList.add('display-astigmatism-panel');
                expandIconAstigmatism.classList.add('hide');
                reduceIconAstigmatism.classList.remove('hide');
            }
        };

        // @ts-expect-error
        window.multifocalClicked = () => {
            var element = document.getElementsByClassName('multifocal-content-panel')[0];
            var expandIconMultifocal = document.getElementsByClassName(
                'expand-icon-multifocal-container'
            )[0];
            var reduceIconMultifocal = document.getElementsByClassName(
                'reduce-icon-multifocal-container'
            )[0];

            if (element.classList.contains('display-multifocal-panel')) {
                element.classList.remove('display-multifocal-panel');
                expandIconMultifocal.classList.remove('hide');
                reduceIconMultifocal.classList.add('hide');
            } else {
                element.classList.add('display-multifocal-panel');
                expandIconMultifocal.classList.add('hide');
                reduceIconMultifocal.classList.remove('hide');
            }
        };

        return () => {
            // @ts-expect-error
            delete window.astigmatismClicked;
            // @ts-expect-error
            delete window.multifocalClicked;
        };
    }, []);

    const [powerOD, setPowerOD] = useState(null);
    const [powerOS, setPowerOS] = useState(null);
    const [bcOD, setBcOD] = useState(null);
    const [bcOS, setBcOS] = useState(null);
    const [diaOD, setDiaOD] = useState(null);
    const [diaOS, setDiaOS] = useState(null);
    const [cylOD, setCylOD] = useState(null);
    const [cylOS, setCylOS] = useState(null);
    const [colorOD, setColorOD] = useState(null);
    const [colorOS, setColorOS] = useState(null);
    const [addOD, setAddOD] = useState(null);
    const [addOS, setAddOS] = useState(null);
    const [dominanceOD, setDominanceOD] = useState(null);
    const [dominanceOS, setDominanceOS] = useState(null);
    const [axisOD, setAxisOD] = useState(null);
    const [axisOS, setAxisOS] = useState(null);
    const [boxOD, setBoxOD] = useState(null);
    const [boxOS, setBoxOS] = useState(null);

    useEffect(() => {
        props.setBackToStepTwoPowerOD(powerOD)
    }, [powerOD])

    useEffect(() => {
        props.setBackToStepTwoPowerOS(powerOS)
    }, [powerOS])

    useEffect(() => {
        props.setBackToStepTwoBcOD(bcOD)
    }, [bcOD])

    useEffect(() => {
        props.setBackToStepTwoBcOS(bcOS)
    }, [bcOS])

    useEffect(() => {
        props.setBackToStepTwoDiaOD(diaOD)
    }, [diaOD])

    useEffect(() => {
        props.setBackToStepTwoDiaOS(diaOS)
    }, [diaOS])

    useEffect(() => {
        props.setBackToStepTwoCylOD(cylOD)
    }, [cylOD])

    useEffect(() => {
        props.setBackToStepTwoCylOS(cylOS)
    }, [cylOS])

    useEffect(() => {
        props.setBackToStepTwoColorOD(colorOD)
    }, [colorOD])

    useEffect(() => {
        props.setBackToStepTwoColorOS(colorOS)
    }, [colorOS])

    useEffect(() => {
        props.setBackToStepTwoAddOD(addOD)
    }, [addOD])

    useEffect(() => {
        props.setBackToStepTwoAddOS(addOS)
    }, [addOS])

    useEffect(() => {
        props.setBackToStepTwoDomOD(dominanceOD)
    }, [dominanceOD])

    useEffect(() => {
        props.setBackToStepTwoDomOS(dominanceOS)
    }, [dominanceOS])

    useEffect(() => {
        props.setBackToStepTwoAxisOD(axisOD)
    }, [axisOD])

    useEffect(() => {
        props.setBackToStepTwoAxisOS(axisOS)
    }, [axisOS])

    // keeping of data inserted
    useEffect(() => {
        if(!!props.backToStepTwoFirstName){
            setValue('firstName', props.backToStepTwoFirstName)
        }

        if(!!props.backToStepTwoLastName){
            setValue('lastName', props.backToStepTwoLastName)
        }

        var success = false

        if(!!props.backToStepTwoDob){
            if(isValid(props.backToStepTwoDob) && compareAsc(props.backToStepTwoDob, new Date('01/01/1900')) !== -1 && compareAsc(props.backToStepTwoDob, new Date()) !== 1 && differenceInYears(new Date(), props.backToStepTwoDob) >= 18){
                setValue('dob', props.backToStepTwoDob)
                success = true;
            }else{
                validateDate(props.backToStepTwoDob)
            }
        }

        setFormStatus({
            ...formStatus,
            firstName: {
                success:!!props.backToStepTwoFirstName && props.backToStepTwoFirstName !== '',
                focus: false,
            },
            lastName: {
                success:!!props.backToStepTwoLastName && props.backToStepTwoLastName !== '',
                focus: false,
            },
            dob: {
                success: !!props.backToStepTwoDob ? success : false,
                focus: false,
            }
        });

        if(!!props.backToStepTwoDoctor){
            setDoctor(props.backToStepTwoDoctor)
        }

        if(!!props.backToStepTwoPowerOD && !!props.isDifferentCLRightFromBack === false){
            setPowerOD(props.backToStepTwoPowerOD)
        }

        if(!!props.backToStepTwoPowerOS && !!props.isDifferentCLLeftFromBack === false){
            setPowerOS(props.backToStepTwoPowerOS)
        }

        if(!!props.backToStepTwoBcOD && !!props.isDifferentCLRightFromBack === false){
            setBcOD(props.backToStepTwoBcOD)
        }

        if(!!props.backToStepTwoBcOS && !!props.isDifferentCLLeftFromBack === false){
            setBcOS(props.backToStepTwoBcOS)
        }

        if(!!props.backToStepTwoDiaOD && !!props.isDifferentCLRightFromBack === false){
            setDiaOD(props.backToStepTwoDiaOD)
        }

        if(!!props.backToStepTwoDiaOS && !!props.isDifferentCLLeftFromBack === false){
            setDiaOS(props.backToStepTwoDiaOS)
        }

        if(!!props.backToStepTwoCylOD && !!props.isDifferentCLRightFromBack === false){
            setCylOD(props.backToStepTwoCylOD)
        }

        if(!!props.backToStepTwoCylOS && !!props.isDifferentCLLeftFromBack === false){
            setCylOS(props.backToStepTwoCylOS)
        }

        if(!!props.backToStepTwoColorOD && !!props.isDifferentCLRightFromBack === false){
            setColorOD(props.backToStepTwoColorOD)
        }

        if(!!props.backToStepTwoColorOS && !!props.isDifferentCLLeftFromBack === false){
            setColorOS(props.backToStepTwoColorOS)
        }

        if(!!props.backToStepTwoAddOD && !!props.isDifferentCLRightFromBack === false){
            setAddOD(props.backToStepTwoAddOD)
        }

        if(!!props.backToStepTwoAddOS && !!props.isDifferentCLLeftFromBack === false){
            setAddOS(props.backToStepTwoAddOS)
        }

        if(!!props.backToStepTwoAxisOD && !!props.isDifferentCLRightFromBack === false){
            setAxisOD(props.backToStepTwoAxisOD)
        }

        if(!!props.backToStepTwoAxisOS && !!props.isDifferentCLLeftFromBack === false){
            setAxisOS(props.backToStepTwoAxisOS)
        }

    }, [setValue])

    const validateDate = (inputDate: Date) => {
        if (
            isValid(inputDate) &&
            compareAsc(inputDate, new Date('01/01/1900')) !== -1 &&
            compareAsc(inputDate, new Date()) !== 1 &&
            differenceInYears(new Date(), inputDate) >= 18
        ) {
            clearErrors('dob');
            console.log('Valid date');

            setValue('dob', inputDate);
            setFormStatus({ ...formStatus, dob: { focus: false, success: true } });
        } else {
            setError('dob', {
                type: 'valueAsDate',
                message:
                    differenceInYears(new Date(), inputDate) < 18
                        ? getLabelFallback(
                            'MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_OF_BIRTH_MESSAGE'
                        )
                        : getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'),
            });
            console.log('Invalid date');
            setValue('dob', inputDate);
            setFormStatus({ ...formStatus, dob: { focus: false, success: false } });
        }
    };

    /** The function below returns the form field error message */
    const getErrorMessage = (field: string): string => {
        switch (field.toUpperCase()) {
            case 'FIRSTNAME':
                return 'MYPROFILE_MY_PRESCRIPTIONS_PATIENT_FIRST_NAME';
            case 'LASTNAME':
                return 'MYPROFILE_MY_PRESCRIPTIONS_PATIENT_LAST_NAME';
            default:
                return `MYPROFILE_MY_PRESCRIPTIONS_PATIENT_${field.toUpperCase()}`;
        }
    };

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;

        if (e.type === 'focus') {
            clearErrors(event.name as keyof Form);
            setFormStatus({
                ...formStatus,
                [event.name]: { success: event.value.trim() !== '', focus: true },
            });
        }

        if (e.type === 'blur') {
            if (['firstName', 'lastName'].includes(event.name)) {
                setFormStatus({
                    ...formStatus,
                    [event.name]: {
                        success: event.value.trim() !== '' && isValidNameOnlyLetters(event.value),
                        focus: false,
                    },
                });
                setValue(event.name as keyof Form, event.value.trim());
                if (!event.value.trim()) {
                    setError(event.name as keyof Form, {
                        type: 'required',
                        message: replaceLabelPlaceholder(
                            'MYPROFILE_MY_PRESCRIPTIONS_FIELD_CANNOT_BE_EMPTY_NAME',
                            '{FORM_FIELD}',
                            getLabelFallback(getErrorMessage(event.name))
                        ),
                    });
                }
                if (!isValidNameOnlyLetters(event.value)) {
                    setError(event.name as keyof Form, {
                        type: 'invalid',
                        message: getLabelFallback(
                            event.name.includes('firstName')
                                ? 'MYPROFILE_MY_PRESCRIPTIONS_CL_FIRST_NAME_ERROR'
                                : 'MYPROFILE_MY_PRESCRIPTIONS_CL_LAST_NAME_ERROR'
                        ),
                    });
                }
            } else {
                setFormStatus({
                    ...formStatus,
                    [event.name]: { success: event.value.trim() !== '', focus: false },
                });
                setValue(event.name as keyof Form, event.value.trim());
                if (!event.value.trim()) {
                    setError(event.name as keyof Form, {
                        type: 'required',
                        message: replaceLabelPlaceholder(
                            'MYPROFILE_MY_PRESCRIPTIONS_FIELD_CANNOT_BE_EMPTY_NAME',
                            '{FORM_FIELD}',
                            getLabelFallback(getErrorMessage(event.name))
                        ),
                    });
                }
            }
        }
    };

    const onSubmit = (data: Form) => {
        validateForm();
        console.log(data);
    };

    const validateForm = () => {
        let setErrorForm = {};
        setPrescriptionErrorAll(false);

        if (watchFirstName.trim() === '') {
            setError('firstName' as keyof Form, {
                type: 'required',
                message: replaceLabelPlaceholder(
                    'MYPROFILE_MY_PRESCRIPTIONS_FIELD_CANNOT_BE_EMPTY_NAME',
                    '{FORM_FIELD}',
                    getLabelFallback(getErrorMessage('firstName'))
                ),
            });
            setErrorForm = {
                ...setErrorForm,
                firstName: 'error',
            };
        }
        if (watchLastName.trim() === '') {
            setError('lastName' as keyof Form, {
                type: 'required',
                message: replaceLabelPlaceholder(
                    'MYPROFILE_MY_PRESCRIPTIONS_FIELD_CANNOT_BE_EMPTY_NAME',
                    '{FORM_FIELD}',
                    getLabelFallback(getErrorMessage('lastName'))
                ),
            });
            setErrorForm = {
                ...setErrorForm,
                lastName: 'error',
            };
        }

        const dob = getValues().dob;

        if (dob == null) {
            setError('dob' as keyof Form, {
                type: 'required',
                message: replaceLabelPlaceholder(
                    'MYPROFILE_MY_PRESCRIPTIONS_FIELD_CANNOT_BE_EMPTY_NAME',
                    '{FORM_FIELD}',
                    getLabelFallback(getErrorMessage('dob'))
                ),
            });
            setErrorForm = {
                ...setErrorForm,
                dob: 'error',
            };
        }

        /* Check OD */
        if (clDetailRight?.attributes?.power?.length > 0 && (powerOD === '-' || powerOD === null)) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_power: 'error',
            };
        }
        if (clDetailRight?.attributes?.baseCurve?.length > 0 && (bcOD === '-' || bcOD === null)) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_bc: 'error',
            };
        }
        if (clDetailRight?.attributes?.diameter?.length > 0 && (diaOD === '-' || diaOD === null)) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_dia: 'error',
            };
        }
        if (clDetailRight?.attributes?.cylinder?.length > 0 && (cylOD === '-' || cylOD === null)) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_cyl: 'error',
            };
        }
        if (clDetailRight?.attributes?.axis?.length > 0 && (axisOD === '-' || axisOD === null)) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_axis: 'error',
            };
        }
        if (
            clDetailRight?.attributes?.add?.length > 0 &&
            (addOD === '-' || addOD === '' || addOD === null)
        ) {
            setPrescriptionErrorAll(true);
            setErrorForm = {
                ...setErrorForm,
                right_eye_add: 'error',
            };
        }

        /* Validate OS */
        if (!!clDetailLeft) {
            if (
                clDetailLeft?.attributes?.power?.length > 0 &&
                (powerOS === '-' || powerOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_power: 'error',
                };
            }
            if (
                clDetailLeft?.attributes?.baseCurve?.length > 0 &&
                (bcOS === '-' || bcOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_bc: 'error',
                };
            }
            if (
                clDetailLeft?.attributes?.diameter?.length > 0 &&
                (diaOS === '-' || diaOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_dia: 'error',
                };
            }
            if (
                clDetailLeft?.attributes?.cylinder?.length > 0 &&
                (cylOS === '-' || cylOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_cyl: 'error',
                };
            }
            if (clDetailLeft?.attributes?.axis?.length > 0 && (axisOS === '-' || axisOS === null)) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_axis: 'error',
                };
            }
            if (
                clDetailLeft?.attributes?.add?.length > 0 &&
                (addOS === '-' || addOS === '' || addOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_add: 'error',
                };
            }
        } else {
            if (
                clDetailRight?.attributes?.power?.length > 0 &&
                (powerOS === '-' || powerOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_power: 'error',
                };
            }
            if (
                clDetailRight?.attributes?.baseCurve?.length > 0 &&
                (bcOS === '-' || bcOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_bc: 'error',
                };
            }
            if (
                clDetailRight?.attributes?.diameter?.length > 0 &&
                (diaOS === '-' || diaOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_dia: 'error',
                };
            }
            if (
                clDetailRight?.attributes?.cylinder?.length > 0 &&
                (cylOS === '-' || cylOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_cyl: 'error',
                };
            }
            if (
                clDetailRight?.attributes?.axis?.length > 0 &&
                (axisOS === '-' || axisOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_axis: 'error',
                };
            }
            if (
                clDetailRight?.attributes?.add?.length > 0 &&
                (addOS === '-' || addOS === '' || addOS === null)
            ) {
                setPrescriptionErrorAll(true);
                setErrorForm = {
                    ...setErrorForm,
                    left_eye_add: 'error',
                };
            }
        }

        //Check Doctor is present
        if (doctor === null) {
            setDoctorError(true);
            setErrorForm = {
                ...setErrorForm,
                doctor: 'error',
            };
        } else {
            setDoctorError(false);
        }

        //Check Upload file is present
        if (isUploadClicked && uploadFile === null) {
            setUploadFileError(true);
            setErrorForm = {
                ...setErrorForm,
                uploadFile: 'error',
            };
        } else {
            setUploadFileError(false);
        }

        //checkbox
        if (!isCheckConfirm) {
            setErrorForm = {
                ...setErrorForm,
                check_confirm: 'error',
            };
        }

        console.log('Validation errors', setErrorForm);

        if (Object.entries(setErrorForm).length === 0) {
            submitPrescription();
        }

        setFormErrors(setErrorForm);
    };

    const submitPrescription = () => {
        let rightSKU = '';
        let leftSKU = '';

        setUploadPrescriptionCl(true);
        setErrorOnCreate(null);

        /* call for right and left sku*/
        const payloadRightSKU: GetSKUPayload = {
            attributes: {
                ...(diaOD != null && diaOD != undefined && !!diaOD.toString() && { CL_DIAMETER: diaOD.toString() }),
                ...(powerOD != null && powerOD != undefined && !!powerOD.toString() && { CL_SPHERE_POWER: powerOD.toString() }),
                ...(bcOD != null && bcOD != undefined && !!parseFloat(bcOD) && !!parseFloat(bcOD).toString() && { CL_BASE_CURVE: parseFloat(bcOD).toString() }),
                ...(axisOD != null && axisOD != undefined && !!axisOD.toString() && { CL_AXIS: axisOD.toString().padStart(3, '0') }),
                ...(cylOD != null && cylOD != undefined && !!cylOD.toString() && { CL_CYLINDER: cylOD.toString() }),
                ...(!!colorOD && { CL_COLOR: colorOD.toString() }),
                ...(!!addOD && { CL_ADDITION: addOD.toString() }),
                ...(!!dominanceOD && { CL_DOMINANCE: dominanceOD.toString()==='D' ? 'True' : 'False'  }),
            },
        };

        const payloadLeftSKU: GetSKUPayload = {
            attributes: {
                ...(diaOS != null && diaOS != undefined && !!diaOS.toString() && { CL_DIAMETER: diaOS.toString() }),
                ...(powerOS != null && powerOS != undefined && !!powerOS.toString() && { CL_SPHERE_POWER: powerOS.toString() }),
                ...(bcOS != null && bcOS != undefined && !!parseFloat(bcOS) && !!parseFloat(bcOS).toString() && { CL_BASE_CURVE: parseFloat(bcOS).toString() }),
                ...(axisOS != null && axisOS != undefined && !!axisOS.toString() && { CL_AXIS: axisOS.toString().padStart(3, '0')}),
                ...(cylOS != null && cylOS != undefined && !!cylOS.toString() && { CL_CYLINDER: cylOS.toString() }),
                ...(!!colorOS && { CL_COLOR: colorOS.toString() }),
                ...(!!addOS && { CL_ADDITION: addOS.toString() }),
                ...(!!dominanceOS && { CL_DOMINANCE: dominanceOS.toString()==='D' ? 'True' : 'False' }),
            },
        };

        getSKU(config.storeId, config.catalogId, props.selectedRightCL, payloadRightSKU)
            .then((res: any) => {
                console.log("RESPONSE THAT COME", JSON.stringify(res));

                if (res?.data?.productId) {
                    rightSKU = res?.data?.productId;

                    getSKU(
                        config.storeId,
                        config.catalogId,
                        !!props.selectedLeftCL ? props.selectedLeftCL : props.selectedRightCL,
                        payloadLeftSKU
                    )
                        .then((res: any) => {
                            if (res?.data?.productId) {
                                leftSKU = res?.data?.productId;

                                //after add prescription
                                let payload: IAddUpdatePrescriptionPayload = {
                                    prescriptionId:
                                        props.isEditingPrescription && props?.editedPrescription
                                            ? props?.editedPrescription.prescriptionId
                                            : null,
                                    clinic:
                                        !!doctor && !!doctor?.officeName
                                            ? doctor?.officeName
                                            : null,
                                    prescriptionType: null,
                                    checksum: null /* Handled by backend logic */,
                                    segmentHeight: null,
                                    doctor: !!doctor && !!doctor?.name ? doctor?.name : null,
                                    osNear: null,
                                    verified: null /* Handled by backend logic */,
                                    birthDate: getStringFromDate(getValues().dob, 'MM/dd/yyyy'),
                                    doctorFax:
                                        !!doctor && !!doctor?.faxNumber ? doctor?.faxNumber : null,
                                    expiration: null,
                                    osOcHeight: null,
                                    osSegHeight: null,
                                    odFar: null,
                                    doctorAddress:
                                        !!doctor && !!doctor?.address
                                            ? doctor?.address?.address1 +
                                            ' ' +
                                            doctor?.address?.city +
                                            ', ' +
                                            doctor?.address?.state +
                                            ' ' +
                                            doctor?.address?.zipPostalCode
                                            : null,
                                    frameRearVetexDistance: null,
                                    rightCatentryId: rightSKU,
                                    leftCatentryId: leftSKU,
                                    comment: comment,
                                    markIfDeleted: null /* Handled by backend logic */,
                                    odOcHeight: null,
                                    fileName: isUploadClicked
                                        ? uploadFile?.name.replace(/ /g, '')
                                        : null,
                                    prescriptionOriginMode: isUploadClicked
                                        ? PrescriptionOriginMode.UPLOAD_PRESCRIPTION
                                        : PrescriptionOriginMode.CALL_DOCTOR,
                                    productCategory: PrescriptionProductCategory.CONTACTS,
                                    issue: null,
                                    statePrescription:
                                        !!doctor && !!doctor?.address?.state
                                            ? doctor?.address?.state
                                            : null,
                                    osFar: null,
                                    odNear: null,
                                    isVisible: null /* Handled by backend logic */,
                                    frameWrap: null,
                                    odSegHeight: null,
                                    framePantoTilt: null,
                                    prescriptionClass:
                                        PrescriptionClass.MY_ACCOUNT /* Handled by backend logic */,
                                    telephone:
                                        !!doctor && !!doctor?.phoneNumber
                                            ? doctor?.phoneNumber
                                            : null,
                                    prescriptionOrigin: PrescriptionOrigin.DOCTOR,
                                    firstName: watchFirstName,
                                    lastName: watchLastName,
                                    nickname: PRESCRIPTION_DEFAULT_NICKNAME,
                                    userId: config.userId,
                                    rightSphere: null,
                                    leftSphere: null,
                                    rightCyl: null,
                                    leftCyl: null,
                                    rightAxis: null,
                                    leftAxis: null,
                                    rightAdd: null,
                                    leftAdd: null,
                                    rightColor: null,
                                    leftColor: null,
                                    prismEnable: false,
                                    odVPrism: null,
                                    osVPrism: null,
                                    odVPrismBaseDir: null,
                                    osVPrismBaseDir: null,
                                    odHPrism: null,
                                    osHPrism: null,
                                    odHPrismBaseDir: null,
                                    osHPrismBaseDir: null,
                                    lastUpdate: null /* Handled by backend logic */,
                                    lastCreate: null /* Handled by backend logic */,
                                    pupillaryDistance: null,
                                    rPDistance: null,
                                    lPDistance: null,
                                    prescriptionName: watchFirstName + ' ' + watchLastName,
                                };

                                if (!!props.isEditingPrescription && !!props?.editedPrescription) {
                                    if (isUploadClicked) {
                                        const payloadUploadFile: UploadPrescriptionFilePayload = {
                                            storeId: config.storeId,
                                            langId: config.langId,
                                            catalogId: config.catalogId,
                                            prescriptionId:
                                                props?.editedPrescription.prescriptionId.toString(),
                                            fileName: clearPrescriptionFileName(
                                                uploadFile.name,
                                                props?.editedPrescription.prescriptionId
                                            ),
                                            fileData: getFileDataFromBase64(uploadFileImage),
                                        };

                                        // Upload file before editing prescription
                                        uploadPrescriptionFile(config.storeId, payloadUploadFile)
                                            .then((res: any) => {
                                                if (res?.data?.fileName) {
                                                    // Update the file name in edit prescription payload
                                                    payload.fileName = res?.data?.fileName;

                                                    // Edit prescription
                                                    editPrescription(
                                                        config.storeId,
                                                        props?.editedPrescription.prescriptionId.toString(),
                                                        payload
                                                    )
                                                        .then((res: any) => {
                                                            if (res?.data?.prescriptionId) {
                                                                setTimeout(() => {
                                                                    dispatch(
                                                                        actions.setJustUpdateId(
                                                                            res?.data
                                                                                ?.prescriptionId +
                                                                            ';'
                                                                        )
                                                                    );
                                                                    navigate(
                                                                        config.pagesUrl.myAppointments
                                                                            .myPrescriptions,
                                                                        {
                                                                            state: {
                                                                                toggleClProp: true,
                                                                            },
                                                                        }
                                                                    );
                                                                }, 1000);

                                                                setIsSavingPrescription(true);

                                                                try {
                                                                    window?.tealium_data2track.push(
                                                                        {
                                                                            id: 'Event',
                                                                            Events_SavePrescriptionCL:
                                                                                '1',
                                                                            Event_Source:
                                                                                'MyAccount Prescription Save',
                                                                        }
                                                                    );
                                                                } catch (e) {
                                                                    console.error(e);
                                                                }
                                                            } else {
                                                                console.error(
                                                                    '>>> Error on edit Prescription - Response Error - Edit prescription'
                                                                );
                                                            }
                                                        })
                                                        .catch(err => {
                                                            console.error(
                                                                '>>> Error on edit prescription CL - Edit prescription',
                                                                err.status,
                                                                ', error: ',
                                                                err
                                                            );
                                                        });
                                                } else {
                                                    console.error(
                                                        '>>> Error on edit prescription - Response Error - Upload prescription file'
                                                    );
                                                }
                                            })
                                            .catch(err => {
                                                console.error(
                                                    '>>> Error on edit prescription CL - Upload prescription file',
                                                    err.status,
                                                    ', error: ',
                                                    err
                                                );
                                            });
                                    } else {
                                        // Edit prescription
                                        editPrescription(
                                            config.storeId,
                                            props?.editedPrescription.prescriptionId.toString(),
                                            payload
                                        )
                                            .then((res: any) => {
                                                if (res?.data?.prescriptionId) {
                                                    setTimeout(() => {
                                                        navigate(
                                                            config.pagesUrl.myAppointments
                                                                .myPrescriptions,
                                                            { state: { toggleClProp: true } }
                                                        );
                                                    }, 1000);

                                                    setIsSavingPrescription(true);

                                                    try {
                                                        window?.tealium_data2track.push({
                                                            id: 'Event',
                                                            Events_SavePrescriptionCL: '1',
                                                            Event_Source:
                                                                'MyAccount Prescription Save',
                                                        });
                                                    } catch (e) {
                                                        console.error(e);
                                                    }
                                                } else {
                                                    console.error(
                                                        '>>> Error on edit Prescription - Response Error - Edit prescription'
                                                    );
                                                }
                                            })
                                            .catch(err => {
                                                console.error(
                                                    '>>> Error on edit prescription CL - Edit prescription',
                                                    err.status,
                                                    ', error: ',
                                                    err
                                                );
                                            });
                                    }
                                } else {
                                    addPrescription(config.storeId, payload)
                                        .then((res: any) => {
                                            if (res?.data?.prescriptionId) {
                                                if (isUploadClicked) {
                                                    const payloadUploadFile: UploadPrescriptionFilePayload =
                                                    {
                                                        storeId: config.storeId,
                                                        langId: config.langId,
                                                        catalogId: config.catalogId,
                                                        prescriptionId:
                                                            res?.data?.prescriptionId,
                                                        fileName: clearPrescriptionFileName(
                                                            uploadFile.name,
                                                            res?.data?.prescriptionId
                                                        ),
                                                        fileData:
                                                            getFileDataFromBase64(
                                                                uploadFileImage
                                                            ),
                                                    };

                                                    // Upload file after adding prescription
                                                    uploadPrescriptionFile(
                                                        config.storeId,
                                                        payloadUploadFile
                                                    )
                                                        .then((res: any) => {
                                                            if (res?.data?.fileName) {
                                                                setTimeout(() => {
                                                                    navigate(
                                                                        config.pagesUrl.myAppointments
                                                                            .myPrescriptions,
                                                                        {
                                                                            state: {
                                                                                toggleClProp: true,
                                                                            },
                                                                        }
                                                                    );
                                                                }, 1000);

                                                                setIsSavingPrescription(true);

                                                                try {
                                                                    window?.tealium_data2track.push(
                                                                        {
                                                                            id: 'Event',
                                                                            Events_SavePrescriptionCL:
                                                                                '1',
                                                                            Event_Source:
                                                                                'MyAccount Prescription Save',
                                                                        }
                                                                    );
                                                                } catch (e) {
                                                                    console.error(e);
                                                                }
                                                            } else {
                                                                console.error(
                                                                    '>>> Error on add Prescription - Response Error - Upload prescription file'
                                                                );

                                                                setErrorOnCreate('UPLOAD');
                                                                setUploadPrescriptionCl(false);
                                                            }
                                                        })
                                                        .catch(err => {
                                                            console.error(
                                                                '>>> Error on add Prescription CL - Upload prescription file',
                                                                err.status,
                                                                ', error: ',
                                                                err
                                                            );

                                                            setErrorOnCreate('UPLOAD');
                                                            setUploadPrescriptionCl(false);
                                                        });
                                                } else {
                                                    setTimeout(() => {
                                                        navigate(
                                                            config.pagesUrl.myAppointments
                                                                .myPrescriptions,
                                                            { state: { toggleClProp: true } }
                                                        );
                                                    }, 1000);

                                                    try {
                                                        window?.tealium_data2track.push({
                                                            id: 'Event',
                                                            Events_SavePrescriptionCL: '1',
                                                            Event_Source:
                                                                'MyAccount Prescription Save',
                                                        });
                                                    } catch (e) {
                                                        console.error(e);
                                                    }

                                                    setIsSavingPrescription(true);
                                                }

                                                setUploadPrescriptionCl(true);
                                            } else {
                                                console.error(
                                                    '>>> Error on add Prescription - Response Error - Add Prescription'
                                                );

                                                setErrorOnCreate('POST');
                                                setUploadPrescriptionCl(false);
                                            }
                                        })
                                        .catch(err => {
                                            console.error(
                                                '>>> Error on add Prescription CL - Add Prescription',
                                                err.status,
                                                ', error: ',
                                                err
                                            );

                                            setErrorOnCreate('POST');
                                            setUploadPrescriptionCl(false);
                                        });
                                }
                            } else {
                                console.error(
                                    '>>> Error on add Prescription - Response Error - Retrieve SKU for left CL'
                                );
                                setErrorOnCreate('SKU_LEFT');
                                setUploadPrescriptionCl(false);
                            }
                        })
                        .catch(err => {
                            console.error(
                                '>>> Error on add Prescription CL - Retrieve SKU for left CL',
                                err.status,
                                ', error: ',
                                err
                            );
                            setErrorOnCreate('SKU_LEFT');
                            setUploadPrescriptionCl(false);
                        });
                } else {
                    console.error(
                        '>>> Error on add Prescription - Response Error - Retrieve SKU for right CL'
                    );
                    setErrorOnCreate('SKU_RIGHT');
                    setUploadPrescriptionCl(false);
                }
            })
            .catch(err => {
                console.error(
                    '>>> Error on add Prescription CL - Retrieve SKU for right CL',
                    err.status,
                    ', error: ',
                    err
                );
                setErrorOnCreate('SKU_RIGHT');
                setUploadPrescriptionCl(false);
            });
    };

    const handleOpenModal = () => {
        setIsHowToReadModalOpen(true);
        document.body.style.overflow = 'hidden'
    }

    const handleCloseModal = () => {
        setIsHowToReadModalOpen(false);
        document.body.style.overflow = ''
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group">
                    <TextField
                        {...register('firstName')}
                        className={`${errors?.firstName?.message
                            ? 'form-field-error'
                            : formStatus.firstName.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="firstName"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PATIENT_FIRST_NAME')
                        )}
                        InputLabelProps={{
                            shrink: Boolean(watch('firstName') || formStatus.firstName.focus),
                        }}
                        InputProps={{
                            endAdornment: formStatus.firstName.focus ? (
                                <InputAdornment position="end">
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('firstName', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.firstName?.message ? (
                                <InputAdornment position="end">
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formStatus.firstName.success ? (
                                <InputAdornment position="end">
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                        helperText={
                            errors?.firstName?.message && (
                                <span className="prescription-name-error">
                                    {errors?.firstName?.message}
                                </span>
                            )
                        }
                    />
                    <TextField
                        {...register('lastName')}
                        className={`${errors?.lastName?.message
                            ? 'form-field-error'
                            : formStatus.lastName.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="lastName"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PATIENT_LAST_NAME')
                        )}
                        InputLabelProps={{
                            shrink: Boolean(watch('lastName') || formStatus.lastName.focus),
                        }}
                        InputProps={{
                            endAdornment: formStatus.lastName.focus ? (
                                <InputAdornment position="end">
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('lastName', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.lastName?.message ? (
                                <InputAdornment position="end">
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formStatus.lastName.success ? (
                                <InputAdornment position="end">
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                        helperText={
                            errors?.lastName?.message && (
                                <span className="prescription-name-error">
                                    {errors?.lastName?.message}
                                </span>
                            )
                        }
                    />
                    <Controller
                        control={control}
                        name="dob"
                        render={({ field: { value } }) => (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    minDate={new Date('01/01/1900')}
                                    disableFuture
                                    className={`${errors?.dob?.message
                                        ? 'form-field-error'
                                        : formStatus.dob.success
                                            ? 'form-field-success'
                                            : ''
                                        }`}
                                    label={capitalizeFirstLetter(getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PATIENT_DOB'))}
                                    open={isOpenDatePicker}
                                    onClose={() => setIsOpenDatePicker(false)}
                                    onAccept={date => validateDate(new Date(date))}
                                    openTo="year"
                                    views={['year', 'month', 'day']}
                                    inputFormat="MM/dd/yyyy"
                                    value={value}
                                    onChange={() => { }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            helperText={errors?.dob?.message}
                                            name="dob"
                                            variant="outlined"
                                            onBlur={(e: SyntheticEvent) => {
                                                try {
                                                    validateDate(new Date((e.target as HTMLInputElement).value));
                                                } catch (error) {
                                                    console.warn(error);
                                                }
                                            }}
                                            error={Boolean(errors?.dob?.message)}
                                            InputProps={{
                                                endAdornment: formStatus.dob.focus && watchDob ? (
                                                    <InputAdornment className="large" position="end">
                                                        <CloseIcon
                                                            className="cursor-pointer"
                                                            onMouseDown={() => setValue('dob', null)}
                                                        />
                                                        <CalendarIcon
                                                            className="input-field-icon"
                                                            onClick={() => setIsOpenDatePicker(true)}
                                                        />
                                                    </InputAdornment>
                                                ) : errors?.dob?.message ? (
                                                    <InputAdornment className="large" position="end">
                                                        <ErrorIcon />
                                                        <CalendarIcon
                                                            className="input-field-icon"
                                                            onClick={() => setIsOpenDatePicker(true)}
                                                        />
                                                    </InputAdornment>
                                                ) : formStatus.dob.success ? (
                                                    <InputAdornment className="large" position="end">
                                                        <SuccessIcon />
                                                        <CalendarIcon
                                                            className="input-field-icon"
                                                            onClick={() => setIsOpenDatePicker(true)}
                                                        />
                                                    </InputAdornment>
                                                ) : (
                                                    <CalendarIcon
                                                        className="input-field-icon medium"
                                                        onClick={() => setIsOpenDatePicker(true)}
                                                    />
                                                ),
                                                style: {
                                                    textTransform: 'uppercase',
                                                }
                                            }}
                                        />
                                    )}
                                />


                            </LocalizationProvider>
                        )}
                    />
                </div>

                {/* How to read your prescription */}
                <div className="how-to-read">
                    <p>
                        <InfoIcon />
                        <a
                            className="link link-underline how-to-read-contact"
                            onClick={() => handleOpenModal()}
                            data-analytics_available_call="0"
                            data-element-id="MyPrescription_HowToRead"
                        >
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_HOW_TO_READ')}
                        </a>
                    </p>
                </div>

                {/* How to read prescription - modal */}
                {isHowToReadModalOpen && 
                    <div className="how-to-read-modal-external-container" data-analytics_available_call="0">
                        <Modal
                            customStyle={isMobile ? 'pd-modal-sm' : ''}
                            isOpen={isHowToReadModalOpen}
                            close={() => handleCloseModal()}
                            isCentered
                        >
                            <HowToReadPrescriptionCL />
                        </Modal>
                    </div>
                }

                <PrescriptionContactFormBox
                    prescriptionValuesError={false}
                    doubleProduct={props.checkedDifferentCL}
                    rightCL={clDetailRight}
                    leftCL={clDetailLeft}
                    powerOD={powerOD}
                    setPowerOD={setPowerOD}
                    powerOS={powerOS}
                    setPowerOS={setPowerOS}
                    bcOD={bcOD}
                    setBcOD={setBcOD}
                    bcOS={bcOS}
                    setBcOS={setBcOS}
                    diaOD={diaOD}
                    setDiaOD={setDiaOD}
                    diaOS={diaOS}
                    setDiaOS={setDiaOS}
                    cylOD={cylOD}
                    setCylOD={setCylOD}
                    cylOS={cylOS}
                    setCylOS={setCylOS}
                    axisOD={axisOD}
                    setAxisOD={setAxisOD}
                    axisOS={axisOS}
                    setAxisOS={setAxisOS}
                    boxOD={boxOD}
                    setBoxOD={setBoxOD}
                    boxOS={boxOS}
                    setBoxOS={setBoxOS}
                    colorOD={colorOD}
                    setColorOD={setColorOD}
                    colorOS={colorOS}
                    setColorOS={setColorOS}
                    addOD={addOD}
                    setAddOD={setAddOD}
                    addOS={addOS}
                    setAddOS={setAddOS}
                    dominanceOD={dominanceOD}
                    setDominanceOD={setDominanceOD}
                    dominanceOS={dominanceOS}
                    setDominanceOS={setDominanceOS}
                    formErrors={formErrors}
                    setFormErrors={setFormErrors}
                    errorOnCreate={errorOnCreate}
                    prescriptionErrorAll={prescriptionErrorAll}
                    setPrescriptionErrorAll={setPrescriptionErrorAll}
                />

                {/* Text Area */}
                <div className="text-area-container">
                    <textarea
                        className="text-area-container-textarea"
                        placeholder='Insert here your doctor recommendation or any additional note'
                        value={comment}
                        onChange={handleChangeComment}
                        maxLength={200}
                    />
                    <div className="text-area-container-comment-length">
                        {!!comment ? comment.length : 0}/200
                    </div>
                </div>

                {/* Horizontal line */}
                <div className="prescription-form-separator" />

                <DoctorDetails
                    findDoctorError={doctorError}
                    doctor={doctor}
                    setDoctor={handleSetDoctor}
                />

                {/* Horizontal line */}
                <div className="prescription-form-separator" />

                <HowToSubmit
                    isEditingPrescription={props.isEditingPrescription}
                    prescriptionId={props?.editedPrescription?.prescriptionId}
                    uploadPrescriptionError={uploadFileError}
                    uploadFile={uploadFile}
                    setUploadFile={handleSetUploadFile}
                    uploadFileImage={uploadFileImage}
                    setUploadFileImage={setUploadFileImage}
                    isUploadClicked={isUploadClicked}
                    setIsUploadClicked={setIsUploadClicked}
                />

                {/* Horizontal line */}
                <div className="prescription-form-separator" />

                {/* Confirm valid prescription checkbox */}
                <div className="valid-prescription-checkbox-container">
                    <FormControlLabel
                        control={
                            <Checkbox
                                data-analytics_available_call="0"
                                className="valid-prescription-checkbox"
                                size="small"
                                checked={isCheckConfirm}
                                onClick={() => setIsCheckConfirm(!isCheckConfirm)}
                                style={{
                                    color:
                                        formErrors['check_confirm'] && !isCheckConfirm
                                            ? 'red'
                                            : 'black',
                                }}
                            />
                        }
                        label={
                            <p className="confirm-checkbox-description">
                                {getLabelFallback(
                                    'MYPROFILE_MY_PRESCRIPTIONS_VALID_PRESCRIPTION_CHECKBOX'
                                )}
                            </p>
                        }
                    />
                </div>
                {formErrors['check_confirm'] && !isCheckConfirm && (
                    <div className="prescription-values-error checkbox-confirm">
                        <span>{getLabelFallback('PRESCRIPTION_ERROR_CHECKBOX_CONFIRM')}</span>
                    </div>
                )}
                {/* Save prescription button */}
                <div className="save-button-container">
                    <button
                        data-element-id="MyPrescription_Save"
                        className={
                            'primary-button button-size-medium button-add-prescription' +
                            (uploadPrescriptionCl ? ' loading' : '')
                        }
                        data-analytics_available_call="0"
                        type="submit"
                    >
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SAVE_PRESCRIPTION_BUTTON')}
                        {uploadPrescriptionCl && <PreLoader />}
                    </button>
                    {isSavingPrescription && (
                        <div className="saving-container">
                            <div className="saved-icon">
                                <SavedIcon />
                            </div>
                            <div className="changes-saved">All changes are saved</div>
                        </div>
                    )}
                    {!isSavingPrescription && !!errorOnCreate && !errorOnCreate.includes('SKU') && (
                        <div className="error-label">
                            {getLabelFallback('ERROR_SUBMIT_PRESCRIPTION_' + errorOnCreate)}
                        </div>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default PrescriptionContactForm;
